import { Link, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { homeRouteFor } from '@config/routes';
import { useAuthStore } from '@stores/auth.store';
import { BrandMark, Button, LanguageToggle, ThemeToggle, Badge } from '@shared/components';
import { NotificationBell } from '@features/notifications/components/NotificationBell';

export interface TopNavProps {
  /** Slotted in before the brand mark. The user shell puts the rail's toggles
   *  here; the admin shell has no rail and passes nothing. */
  readonly leading?: React.ReactNode;
}

/** Shared top bar for authenticated app + admin shells. */
export function TopNav({ leading }: TopNavProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const handleSignOut = async (): Promise<void> => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-[var(--nav-bg)] backdrop-blur-md backdrop-saturate-150">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6">
        {leading}
        <Link to={homeRouteFor(user?.role)} className="flex items-center gap-2.5">
          <BrandMark size={26} animated />
          <span className="hidden text-base font-semibold tracking-tight sm:inline">
            {t('nav.brand')}
          </span>
        </Link>

        <div className="ms-auto flex items-center gap-2">
          {user ? <NotificationBell /> : null}
          <LanguageToggle />
          <ThemeToggle />
          {user && (
            <div className="flex items-center gap-2 border-s border-line ps-3">
              <div className="hidden text-end sm:block">
                <p className="text-sm font-medium leading-tight text-text">{user.name}</p>
              </div>
              <Badge tone={user.role === 'admin' ? 'admin' : 'muted'}>
                {user.role === 'admin' ? t('nav.admin') : t('nav.user')}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => void handleSignOut()} aria-label={t('common.signOut')}>
                <LogOut size={16} aria-hidden="true" />
                <span className="hidden sm:inline">{t('common.signOut')}</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
